import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { downloadCSV } from '@/lib/csv';
import { logger } from '@/lib/logger';

// ─────────────────────────────────────────────────────────────
// Logs système — ce que le code de l'application journalise réellement.
//
// Le drapeau `VITE_PUBLIC_ENABLE_SYSTEM_LOGS` du fichier .env active
// l'envoi vers `platform_system_logs`. Ce panneau affiche ces entrées
// (niveau, catégorie, page, utilisateur, données) avec filtres, export
// CSV et rafraîchissement automatique.
// ─────────────────────────────────────────────────────────────

type Period = '1h' | '24h' | '7d' | 'all';

interface SystemLog {
  id: number;
  level: string;
  category: string | null;
  message: string;
  data: Record<string, unknown> | null;
  page: string | null;
  user_id: string | null;
  user_label: string | null;
  user_agent: string | null;
  repeat_count: number | null;
  created_at: string;
}

const PERIOD_LABELS: Record<Period, string> = {
  '1h': '1 heure',
  '24h': '24 heures',
  '7d': '7 jours',
  all: 'Tout',
};

const LEVEL_CONFIG: Record<string, { label: string; icon: string; badge: string; dot: string }> = {
  error: { label: 'Erreur', icon: 'ri-error-warning-line', badge: 'bg-red-50 text-red-600', dot: 'bg-red-500' },
  warn: { label: 'Alerte', icon: 'ri-alert-line', badge: 'bg-amber-50 text-amber-700', dot: 'bg-amber-400' },
  info: { label: 'Info', icon: 'ri-information-line', badge: 'bg-primary-100 text-primary-700', dot: 'bg-primary-400' },
  debug: { label: 'Debug', icon: 'ri-terminal-line', badge: 'bg-background-100 text-foreground-600', dot: 'bg-foreground-300' },
};

function sinceISO(period: Period): string | null {
  if (period === 'all') return null;
  const hours = period === '1h' ? 1 : period === '24h' ? 24 : 24 * 7;
  return new Date(Date.now() - hours * 3600 * 1000).toISOString();
}

function shortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export default function SystemLogsTab() {
  const [logs, setLogs] = useState<SystemLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [period, setPeriod] = useState<Period>('24h');
  const [level, setLevel] = useState('all');
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<number | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [clearing, setClearing] = useState(false);
  const [notice, setNotice] = useState('');

  const remoteEnabled = logger.remoteEnabled;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      let query = supabase
        .from('platform_system_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(600);
      const since = sinceISO(period);
      if (since) query = query.gte('created_at', since);
      const { data, error } = await query;
      if (error) throw error;
      setLogs((data as SystemLog[]) || []);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Impossible de charger les logs système');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    void logger.flushNow();
    void fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const timer = window.setInterval(() => void fetchLogs(), 15000);
    return () => window.clearInterval(timer);
  }, [autoRefresh, fetchLogs]);

  const categories = useMemo(() => {
    const set = new Set<string>();
    logs.forEach((l) => { if (l.category) set.add(l.category); });
    return Array.from(set).sort();
  }, [logs]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return logs.filter((log) => {
      if (level !== 'all' && log.level !== level) return false;
      if (category !== 'all' && log.category !== category) return false;
      if (q) {
        const haystack = `${log.message} ${log.category || ''} ${log.page || ''} ${log.user_label || ''} ${JSON.stringify(log.data || {})}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [logs, level, category, search]);

  const stats = useMemo(() => ({
    total: logs.length,
    errors: logs.filter((l) => l.level === 'error').length,
    warnings: logs.filter((l) => l.level === 'warn').length,
    categories: categories.length,
  }), [logs, categories]);

  const exportCsv = () => {
    if (filtered.length === 0) return;
    const headers = ['Date', 'Niveau', 'Categorie', 'Message', 'Page', 'Utilisateur', 'Repetitions', 'Donnees'];
    const rows = filtered.map((log) => [
      new Date(log.created_at).toISOString(),
      LEVEL_CONFIG[log.level]?.label || log.level,
      log.category || '',
      log.message,
      log.page || '',
      log.user_label || log.user_id || '',
      String(log.repeat_count || 1),
      JSON.stringify(log.data || {}, null, 2),
    ]);
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    downloadCSV(`zifek-logs-systeme-${stamp}.csv`, headers, rows);
  };

  const clearLogs = async () => {
    if (!window.confirm('Supprimer tous les logs système enregistrés ? Cette action est définitive.')) return;
    setClearing(true);
    setNotice('');
    try {
      const oldest = logs[0]?.id;
      let query = supabase.from('platform_system_logs').delete();
      query = oldest ? query.lte('id', oldest) : query.gte('id', 0);
      const { error } = await query;
      if (error) throw error;
      setLogs([]);
      setNotice('Logs système supprimés.');
      window.setTimeout(() => setNotice(''), 3000);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Suppression impossible.');
    } finally {
      setClearing(false);
    }
  };

  return (
    <div>
      <div className={`mb-5 rounded-lg border px-4 py-3 flex items-start gap-3 ${remoteEnabled ? 'border-accent-200/70 bg-accent-50/60' : 'border-amber-200 bg-amber-50'}`}>
        <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${remoteEnabled ? 'bg-accent-100 text-accent-700' : 'bg-amber-100 text-amber-700'}`}>
          <i className={`${remoteEnabled ? 'ri-database-2-line' : 'ri-plug-line'} text-sm`}></i>
        </span>
        <div className="min-w-0">
          <p className="text-xs font-semibold text-foreground-900">
            {remoteEnabled ? 'Enregistrement des logs système activé' : 'Enregistrement des logs système désactivé'}
          </p>
          <p className="text-[11px] text-foreground-600 mt-0.5">
            {remoteEnabled
              ? 'Le code envoie ses entrées (info, alerte, erreur) ici automatiquement. Les messages identiques rapprochés sont regroupés avec un compteur.'
              : 'Ajoutez VITE_PUBLIC_ENABLE_SYSTEM_LOGS="true" dans le fichier .env pour remonter les logs du code dans ce journal.'}
          </p>
        </div>
      </div>

      {notice && (
        <div className="mb-4 px-4 py-2.5 rounded-lg bg-accent-50 text-accent-700 text-xs flex items-center gap-2">
          <i className="ri-checkbox-circle-line"></i>{notice}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        {[
          { icon: 'ri-terminal-line', label: 'Entrées', value: stats.total.toLocaleString(), sub: 'Sur la période sélectionnée', color: 'bg-primary-100 text-primary-700' },
          { icon: 'ri-error-warning-line', label: 'Erreurs', value: stats.errors.toLocaleString(), sub: 'Niveau error', color: 'bg-red-50 text-red-600' },
          { icon: 'ri-alert-line', label: 'Alertes', value: stats.warnings.toLocaleString(), sub: 'Niveau warn', color: 'bg-amber-50 text-amber-700' },
          { icon: 'ri-price-tag-3-line', label: 'Catégories', value: stats.categories.toString(), sub: 'Modules ayant journalisé', color: 'bg-accent-100 text-accent-700' },
        ].map((card) => (
          <div key={card.label} className="bg-background-50 border border-background-200/70 rounded-lg p-4">
            <div className={`w-8 h-8 rounded-lg ${card.color} flex items-center justify-center mb-2`}>
              <i className={`${card.icon} text-sm`}></i>
            </div>
            <div className="text-lg font-bold font-heading text-foreground-950">{card.value}</div>
            <div className="text-xs text-foreground-500">{card.label}</div>
            <div className="text-[11px] text-foreground-400 mt-0.5">{card.sub}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-5">
        <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200/70">
          {(Object.keys(PERIOD_LABELS) as Period[]).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors cursor-pointer ${
                period === p ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              {PERIOD_LABELS[p]}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200/70">
          {['all', 'debug', 'info', 'warn', 'error'].map((l) => (
            <button
              key={l}
              onClick={() => setLevel(l)}
              className={`px-3 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors cursor-pointer ${
                level === l ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              {l === 'all' ? 'Tous' : LEVEL_CONFIG[l]?.label || l}
            </button>
          ))}
        </div>

        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="h-9 px-3 rounded-full border border-background-200/70 bg-background-50 text-[11px] text-foreground-700 focus:outline-none focus:border-primary-300 cursor-pointer"
        >
          <option value="all">Toutes les catégories</option>
          {categories.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>

        <div className="relative flex-1 min-w-0">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un message, une page, une donnée…"
            className="w-full h-9 pl-9 pr-3 rounded-full border border-background-200/70 bg-background-50 text-xs text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
          />
        </div>

        <label className="flex items-center gap-2 text-[11px] text-foreground-600 whitespace-nowrap cursor-pointer">
          <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} className="accent-primary-500" />
          Auto (15 s)
        </label>

        <button
          onClick={exportCsv}
          disabled={filtered.length === 0}
          className="flex items-center gap-1.5 h-9 px-3 rounded-full border border-background-200/70 bg-background-50 text-[11px] font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
        >
          <i className="ri-file-excel-2-line"></i>
          Exporter CSV
          <span className="text-foreground-400">({filtered.length})</span>
        </button>

        <button
          onClick={() => void fetchLogs()}
          className="w-9 h-9 flex items-center justify-center rounded-full border border-background-200/70 bg-background-50 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer flex-shrink-0"
          title="Rafraîchir"
        >
          <i className={`ri-refresh-line text-sm ${loading ? 'animate-spin' : ''}`}></i>
        </button>

        <button
          onClick={() => void clearLogs()}
          disabled={clearing || logs.length === 0}
          className="w-9 h-9 flex items-center justify-center rounded-full border border-background-200/70 bg-background-50 text-red-500 hover:bg-red-50 transition-colors cursor-pointer flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
          title="Supprimer les logs affichés"
        >
          <i className={`${clearing ? 'ri-loader-4-line animate-spin' : 'ri-delete-bin-line'} text-sm`}></i>
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : loadError ? (
        <div className="flex flex-col items-center justify-center py-16 border border-background-200/70 rounded-lg bg-background-50">
          <i className="ri-error-warning-line text-4xl text-red-400 mb-3"></i>
          <p className="text-sm text-foreground-600 mb-3">{loadError}</p>
          <button onClick={() => void fetchLogs()} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-medium cursor-pointer hover:bg-primary-600 transition-colors">
            Réessayer
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border border-background-200/70 rounded-lg bg-background-50">
          <i className="ri-terminal-box-line text-4xl text-foreground-300 mb-3 block"></i>
          <p className="text-sm font-medium text-foreground-700">Aucun log système sur cette période</p>
          <p className="text-xs text-foreground-500 mt-1">Dès que le code journalise une action, elle apparaîtra ici.</p>
        </div>
      ) : (
        <div className="rounded-lg border border-background-200/70 bg-background-50 divide-y divide-background-200/60">
          {filtered.map((log) => {
            const cfg = LEVEL_CONFIG[log.level] || LEVEL_CONFIG.info;
            const isOpen = expanded === log.id;
            return (
              <div key={log.id}>
                <button
                  onClick={() => setExpanded(isOpen ? null : log.id)}
                  className="w-full text-left px-4 py-2.5 flex items-start gap-3 cursor-pointer hover:bg-background-100/40 transition-colors"
                >
                  <span className={`w-6 h-6 rounded-md ${cfg.badge} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                    <i className={`${cfg.icon} text-[11px]`}></i>
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[10px] text-foreground-400 whitespace-nowrap font-mono">{shortDate(log.created_at)}</span>
                      {log.category && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary-100 text-secondary-800 font-medium whitespace-nowrap">
                          {log.category}
                        </span>
                      )}
                      <span className="text-[11px] font-semibold text-foreground-700">{cfg.label}</span>
                      {(log.repeat_count || 1) > 1 && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-background-200/70 text-foreground-600 font-semibold whitespace-nowrap">
                          ×{log.repeat_count}
                        </span>
                      )}
                      {log.page && <span className="text-[10px] text-foreground-400 truncate max-w-[220px] font-mono">{log.page}</span>}
                    </div>
                    <p className="text-xs text-foreground-800 break-words mt-0.5">{log.message}</p>
                  </div>
                  <i className={`${isOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-400 flex-shrink-0`}></i>
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 bg-background-100/30">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3 text-[11px] text-foreground-500">
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-foreground-400 mb-1">Utilisateur</p>
                        <p className="break-all">{log.user_label || log.user_id || 'Visiteur non connecté'}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-foreground-400 mb-1">Navigateur</p>
                        <p className="break-all">{log.user_agent || '—'}</p>
                      </div>
                    </div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-foreground-400 mb-1">Données journalisées</p>
                    <pre className="whitespace-pre-wrap break-words rounded-md bg-background-50 border border-background-200/70 p-3 text-[11px] text-foreground-700 font-mono max-h-64 overflow-auto">
{JSON.stringify(log.data || {}, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}